import { useState } from "react";
import { Tag, Handshake, Coffee, Star, DollarSign, X, Check } from "lucide-react";
import { getEventColor } from "@/lib/chartUtils";

const TYPES = [
  { key: "tag", label: "Tag", Icon: Tag },
  { key: "referral", label: "Referral", Icon: Handshake },
  { key: "oneOnOne", label: "1-on-1", Icon: Coffee },
  { key: "guest", label: "Guest", Icon: Star },
  { key: "biz", label: "Closed $", Icon: DollarSign },
];

// Defined OUTSIDE the component so React treats them as stable types (no unmount/remount)
function Sel({ label, val, onChange, opts, testId }) {
  return (
    <div className="flex-1 min-w-[150px]">
      <label className="block text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-1">{label}</label>
      <select
        data-testid={testId}
        value={val || ""}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 outline-none"
      >
        <option value="">— select —</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Inp({ label, val, onChange, placeholder, testId }) {
  return (
    <div className="flex-1 min-w-[120px]">
      <label className="block text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-1">{label}</label>
      <input
        data-testid={testId}
        value={val || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 outline-none"
      />
    </div>
  );
}

export default function EditDialog({ event, members: rawMembers, onSave, onCancel }) {
  const members = [...rawMembers].sort((a, b) => a.localeCompare(b));
  const [f, setF] = useState({ ...event, amt: event.amt ? String(event.amt) : "" });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  function save() {
    let updated = { ...f };
    if (f.type === "biz") {
      const n = parseInt(String(f.amt).replace(/[$,\s]/g, ""));
      if (!n || n <= 0) return;
      updated.amt = n;
    }
    onSave(updated);
  }

  const allM = [...members, "Unknown", "Unassigned"];

  return (
    <div data-testid="edit-dialog" className="border-2 border-orange-500 rounded-lg overflow-hidden mb-2 bg-white shadow-sm">
      <div className="flex items-center justify-between bg-orange-50 px-4 py-2.5 border-b border-orange-200">
        <span className="text-xs font-extrabold text-orange-700 tracking-wide">Editing Entry</span>
        <button data-testid="edit-cancel-x" onClick={onCancel} className="text-zinc-400 hover:text-zinc-600"><X className="w-4 h-4" /></button>
      </div>

      <div className="p-4 space-y-4">
        {/* Type selector */}
        <div>
          <label className="block text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-2">Change Type</label>
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map(({ key, label, Icon }) => {
              const c = getEventColor(key);
              const active = f.type === key;
              return (
                <button
                  key={key}
                  data-testid={`edit-type-${key}`}
                  onClick={() => set("type", key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all border ${
                    active ? 'bg-zinc-900 text-white border-zinc-900' : `bg-white ${c.text} border-zinc-200 hover:border-zinc-300`
                  }`}
                >
                  <Icon className="w-3 h-3" /> {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Fields based on type */}
        <div className="flex flex-wrap gap-3">
          {f.type === "tag" && <>
            <Sel label="Tagged by (optional)" val={f.giver} onChange={v => set("giver", v)} opts={allM} testId="edit-tag-giver" />
            <Sel label="Receiver — gets credit" val={f.receiver} onChange={v => set("receiver", v)} opts={members} testId="edit-tag-receiver" />
          </>}
          {f.type === "referral" && <>
            <Sel label="Given by" val={f.giver} onChange={v => set("giver", v)} opts={members} testId="edit-ref-giver" />
            <Sel label="Referred to — gets credit" val={f.receiver} onChange={v => set("receiver", v)} opts={members} testId="edit-ref-receiver" />
          </>}
          {f.type === "oneOnOne" && <>
            <Sel label="Person 1" val={f.a} onChange={v => set("a", v)} opts={members} testId="edit-oo-a" />
            <Sel label="Person 2" val={f.b} onChange={v => set("b", v)} opts={members} testId="edit-oo-b" />
            <Inp label="Note (optional)" val={f.note} onChange={v => set("note", v)} placeholder="e.g. with Travis" testId="edit-oo-note" />
          </>}
          {f.type === "guest" && <>
            <Sel label="Invited by" val={f.bringer} onChange={v => set("bringer", v)} opts={[...members, "Website", "Facebook", "Google", "Friend", "Other", "Unassigned"]} testId="edit-guest-bringer" />
            <div className="flex-1 min-w-[150px]">
              <label className="block text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-1">Visit Status</label>
              <select
                data-testid="edit-guest-status"
                value={f.visitStatus || ""}
                onChange={e => set("visitStatus", e.target.value)}
                className="w-full px-3 py-2 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="">— none —</option>
                <option value="scheduled">Scheduled to Visit</option>
                <option value="visited">Visited</option>
              </select>
            </div>
            <Inp label="Visit Date" val={f.visitDate} onChange={v => set("visitDate", v)} placeholder="2026-08-14" testId="edit-guest-visitdate" />
            <Inp label="Guest name" val={f.guestName} onChange={v => set("guestName", v)} placeholder="Name..." testId="edit-guest-name" />
            <Inp label="Business" val={f.guestBiz} onChange={v => set("guestBiz", v)} placeholder="Company..." testId="edit-guest-biz" />
            <Inp label="Phone" val={f.guestPhone} onChange={v => set("guestPhone", v)} placeholder="555-000-0000" testId="edit-guest-phone" />
            <Inp label="Email" val={f.guestEmail} onChange={v => set("guestEmail", v)} placeholder="email@..." testId="edit-guest-email" />
          </>}
          {f.type === "biz" && <>
            <Sel label="Sent by" val={f.sender} onChange={v => set("sender", v)} opts={members} testId="edit-biz-sender" />
            <Sel label="Received by" val={f.receiver} onChange={v => set("receiver", v)} opts={members} testId="edit-biz-receiver" />
            <Inp label="Amount ($)" val={f.amt} onChange={v => set("amt", v)} placeholder="8000" testId="edit-biz-amount" />
          </>}
        </div>

        {f.type === "referral" && (
          <div>
            <label className="block text-[9px] font-bold text-zinc-400 tracking-widest uppercase mb-1">Notes (optional)</label>
            <textarea
              data-testid="edit-ref-notes"
              value={f.notes || ""}
              onChange={e => set("notes", e.target.value)}
              placeholder="Context..."
              className="w-full px-3 py-2 text-sm font-body border border-zinc-200 rounded-md min-h-[50px] resize-y focus:ring-2 focus:ring-orange-500 outline-none"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 px-4 py-3 border-t border-zinc-100">
        <button
          data-testid="edit-cancel-btn"
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-xs font-bold text-zinc-600 border border-zinc-200 hover:bg-zinc-50 transition-colors"
        >
          Cancel
        </button>
        <button
          data-testid="edit-save-btn"
          onClick={save}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 transition-colors"
        >
          <Check className="w-3.5 h-3.5" /> Save Changes
        </button>
      </div>
    </div>
  );
}
